import { Fragment } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import type { MarketplaceProperty } from '../types';

import styles from './PdpSchools.module.css';

type School = MarketplaceProperty['schools'][number];

const GREAT_SCHOOLS_AVERAGE_RATING_MIN = 4;
const GREAT_SCHOOLS_ABOVE_AVERAGE_RATING_MIN = 8;
const GREAT_SCHOOLS_MAX_RATING = 10;
const SCHOOL_LEVEL_ORDER = ['ELEMENTARY', 'MIDDLE', 'HIGH'] as const;

const schoolLevelLabels = {
  ELEMENTARY: 'Elementary',
  MIDDLE: 'Middle',
  HIGH: 'High',
  UNKNOWN: null,
} satisfies Record<NonNullable<School['educationLevels']>[number], string | null>;

const schoolRatingColor = (rating: School['rating']) => {
  if (rating == null) return 'bg-od-bg-hover-primary';
  if (rating < GREAT_SCHOOLS_AVERAGE_RATING_MIN) return 'bg-od-bg-error';
  if (rating < GREAT_SCHOOLS_ABOVE_AVERAGE_RATING_MIN) return 'bg-amber-400';
  return 'bg-od-bg-success';
};

const schoolRatingBadgeColor = (rating: School['rating']) => {
  if (rating == null) return styles.ratingUnavailable;
  if (rating < GREAT_SCHOOLS_AVERAGE_RATING_MIN) return styles.ratingLow;
  if (rating < GREAT_SCHOOLS_ABOVE_AVERAGE_RATING_MIN) {
    return 'bg-amber-100 text-amber-400';
  }
  return styles.ratingHigh;
};

const schoolLevelOrder = (school: School) => {
  const levels = school.educationLevels ?? [];
  const index = SCHOOL_LEVEL_ORDER.findIndex((level) => levels.includes(level));
  return index === -1 ? SCHOOL_LEVEL_ORDER.length : index;
};

function capitalizeSchoolBoundary(_match: string, boundary: string, letter: string) {
  return `${boundary}${letter.toUpperCase()}`;
}

// Listing school names arrive in all caps. Title case is easier to scan. Mixed-case names stay as written.
function schoolDisplayName(name: string) {
  const letters = name.replace(/[^A-Za-z]/g, '');
  if (letters.length === 0 || letters !== letters.toUpperCase()) return name;
  return name.toLowerCase().replace(/(^|[^A-Za-z])([a-z])/g, capitalizeSchoolBoundary);
}

export const PdpSchools = ({
  schools,
  schoolDistricts,
}: Pick<MarketplaceProperty, 'schools' | 'schoolDistricts'>) => {
  if (schools.length === 0 && schoolDistricts.length === 0) return null;
  const orderedSchools = [...schools].sort(
    (left, right) => schoolLevelOrder(left) - schoolLevelOrder(right),
  );
  return (
    <Col as="section" aria-label="Schools" className="gap-6">
      <Text as="h2" className="text-label-lg">
        Schools
      </Text>
      {orderedSchools.length > 0 && (
        <Col>
          {orderedSchools.map((school, index) => {
            const rating = school.rating;
            const ratingColor = schoolRatingColor(rating);
            const name = schoolDisplayName(school.name);
            const levels = SCHOOL_LEVEL_ORDER.filter((level) =>
              school.educationLevels?.includes(level),
            )
              .map((level) => schoolLevelLabels[level])
              .join(', ');
            const grades = school.gradesOffered?.trim();
            const details = [levels, grades && `Grades ${grades}`].filter(Boolean).join(' · ');
            return (
              <Fragment key={school.id}>
                <Row className="items-center justify-between gap-3 py-3.5 first:pt-0 last:pb-0 sm:gap-6">
                  <Col className="min-w-0 gap-1">
                    {school.greatSchoolsUrl ? (
                      <Box
                        href={school.greatSchoolsUrl}
                        external
                        analyticsName="cosmos-marketplace-pdp-school"
                        aria-label={`View ${name} on GreatSchools`}
                        className="min-w-0 truncate whitespace-nowrap text-body-md text-od-content-primary no-underline hover:underline"
                      >
                        {name}
                      </Box>
                    ) : (
                      <Text className="min-w-0 truncate whitespace-nowrap text-body-md text-od-content-primary">
                        {name}
                      </Text>
                    )}
                    {details && (
                      <Text className="text-body-sm text-od-content-tertiary sm:text-od-content-secondary">
                        {details}
                      </Text>
                    )}
                  </Col>
                  <Row className="shrink-0 items-center gap-4">
                    {rating != null && (
                      <Row aria-hidden="true" className="hidden gap-1 sm:flex">
                        {Array.from({ length: GREAT_SCHOOLS_MAX_RATING }, (_, index) => (
                          <Box
                            key={index}
                            className={cn(
                              'h-[26px] w-1.5 rounded-full',
                              index < rating ? ratingColor : 'bg-od-bg-hover-primary',
                            )}
                          />
                        ))}
                      </Row>
                    )}
                    <Text
                      className={cn(
                        'whitespace-nowrap rounded-lg px-2 py-1 text-label-sm sm:hidden',
                        schoolRatingBadgeColor(rating),
                      )}
                    >
                      {rating == null ? '—' : `${rating} / ${GREAT_SCHOOLS_MAX_RATING}`}
                    </Text>
                    <Text className="hidden min-w-12 whitespace-nowrap text-body-md text-od-content-primary sm:flex">
                      {school.rating == null
                        ? 'Not rated'
                        : `${school.rating} / ${GREAT_SCHOOLS_MAX_RATING}`}
                    </Text>
                  </Row>
                </Row>
                {index < orderedSchools.length - 1 && (
                  <Box aria-hidden="true" className="h-px w-full bg-od-border-secondary" />
                )}
              </Fragment>
            );
          })}
        </Col>
      )}
      <Col className="gap-4">
        <Box aria-hidden="true" className="h-px w-full bg-od-border-secondary" />
        <Text className="text-body-xs text-od-content-tertiary">
          {schoolDistricts.length > 0 && (
            <>
              In{' '}
              <Text as="span" className="font-medium text-od-content-primary">
                {schoolDistricts.join(', ')}
              </Text>{' '}
              {schoolDistricts.length === 1 ? 'school district' : 'school districts'}.{' '}
            </>
          )}
          Ratings by{' '}
          <Link
            href="https://www.greatschools.org/"
            target="_blank"
            rel="noopener"
            analyticsName="cosmos-marketplace-pdp-school-attribution"
            className="text-body-xs text-od-content-primary"
          >
            GreatSchools
          </Link>
          .
        </Text>
      </Col>
    </Col>
  );
};
